import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { getAllLampComponents } from '../services/productService';
import { DEFAULT_BASE_COLOR_HEX, DEFAULT_SHADE_COLOR_HEX, PRODUCT_NAME_PREFIX } from '../constants/defaults';
import { generateModelCode } from '../utils/formatters';
import { getColorDisplayName } from '../utils/colorNames';
import './Home.css';

// Elegantly simple animated text character
const AnimatedChar = ({ char, index }) => (
  <motion.span
    initial={{ opacity: 0, y: 30 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{
      duration: 0.4,
      delay: index * 0.02,
      ease: [0.215, 0.61, 0.355, 1],
    }}
    style={{ display: 'inline-block' }}
  >
    {char === ' ' ? '\u00A0' : char}
  </motion.span>
);

// Animated text with character reveal
const AnimatedText = ({ text, className }) => (
  <span className={className}>
    {text.split('').map((char, i) => (
      <AnimatedChar key={i} char={char} index={i} />
    ))}
  </span>
);

export default function Home() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { addToCart } = useCart();
  const heroRef = useRef(null);
  const videoRef = useRef(null);
  const { scrollYProgress } = useScroll();
  const opacity = useTransform(scrollYProgress, [0, 0.2], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.2], [1, 0.95]);

  // Component data for direct add-to-cart
  const [components, setComponents] = useState(null);
  const [cartToast, setCartToast] = useState(null);

  useEffect(() => {
    getAllLampComponents().then(data => {
      const bases = data.bases.map(b => ({ ...b, id: b.id, code: b.productComponentCode, name: b.productComponentName, displayOrder: b.displayOrder, price: parseFloat(b.price) || 0 }));
      const shades = data.shades.map(s => ({ ...s, id: s.id, code: s.productComponentCode, name: s.productComponentName, displayOrder: s.displayOrder, price: parseFloat(s.price) || 0 }));
      const colors = data.colors.map(c => ({ ...c, id: c.productComponentCode, swatch: c.attributes?.hexCode, price: parseFloat(c.price) || 0 }));
      setComponents({ bases, shades, colors });
    }).catch(() => {});
  }, []);

  // Flying image animation state
  const [flyingItem, setFlyingItem] = useState(null);

  // Check if a gallery lamp's components are all still active
  const isLampAvailable = useCallback((lamp) => {
    if (!components) return false;
    const base = components.bases.find(b => String(b.displayOrder) === lamp.base);
    const shade = components.shades.find(s => String(s.displayOrder) === lamp.shade);
    const baseColor = components.colors.find(c => c.id === lamp.baseColor);
    const shadeColor = components.colors.find(c => c.id === lamp.shadeColor);
    return !!(base && shade && baseColor && shadeColor);
  }, [components]);

  const handleQuickAddToCart = useCallback((lamp, e) => {
    if (!components) return;

    if (!isLampAvailable(lamp)) {
      setCartToast('This configuration is currently unavailable');
      setTimeout(() => setCartToast(null), 3000);
      return;
    }

    const base = components.bases.find(b => String(b.displayOrder) === lamp.base);
    const shade = components.shades.find(s => String(s.displayOrder) === lamp.shade);
    const baseColor = components.colors.find(c => c.id === lamp.baseColor);
    const shadeColor = components.colors.find(c => c.id === lamp.shadeColor);

    if (!base || !shade) return;

    // Get source position (the clicked card image)
    const card = e.currentTarget.closest('.featured-card');
    const img = card?.querySelector('.featured-image');
    const cartIcon = document.querySelector('.cart-icon-wrapper');

    if (img && cartIcon) {
      const imgRect = img.getBoundingClientRect();
      const cartRect = cartIcon.getBoundingClientRect();

      setFlyingItem({
        src: lamp.image,
        startX: imgRect.left + imgRect.width / 2,
        startY: imgRect.top + imgRect.height / 2,
        endX: cartRect.left + cartRect.width / 2,
        endY: cartRect.top + cartRect.height / 2,
      });

      setTimeout(() => setFlyingItem(null), 800);
    }

    const cartItem = {
      baseId: base.id,
      shadeId: shade.id,
      baseCode: base.code,
      shadeCode: shade.code,
      baseColor: lamp.baseColor,
      shadeColor: lamp.shadeColor,
      baseColorHex: baseColor?.swatch || DEFAULT_BASE_COLOR_HEX,
      shadeColorHex: shadeColor?.swatch || DEFAULT_SHADE_COLOR_HEX,
      baseName: base.name,
      shadeName: shade.name,
      baseColorName: getColorDisplayName(baseColor?.id) || 'Default',
      shadeColorName: getColorDisplayName(shadeColor?.id) || 'Default',
      modelCode: generateModelCode(base.code, shade.code),
      modelName: `${PRODUCT_NAME_PREFIX} ${base.name} × ${shade.name}`,
      price: base.price + shade.price + (baseColor?.price || 0) + (shadeColor?.price || 0),
      previewImage: lamp.image,
    };

    addToCart(cartItem);
    setCartToast(`${base.name} × ${shade.name} added to cart!`);
    setTimeout(() => setCartToast(null), 3000);
  }, [components, addToCart, isLampAvailable]);

  // Ensure video plays on mount and when returning to page
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const playVideo = async () => {
      try {
        video.playbackRate = 0.75; // Slow down video to 75% speed
        await video.play();
        console.log('Video playing successfully');
      } catch (error) {
        console.log('Video autoplay prevented:', error);
      }
    };

    // Try to play when video is loaded
    const handleLoadedData = () => {
      playVideo();
    };

    // Set playback rate immediately
    video.playbackRate = 0.75;

    // If video is already loaded, play immediately
    if (video.readyState >= 3) {
      playVideo();
    } else {
      // Otherwise wait for loadeddata event
      video.addEventListener('loadeddata', handleLoadedData);
    }

    // Fallback: try on any user interaction
    const handleInteraction = () => {
      playVideo();
      document.removeEventListener('click', handleInteraction, { once: true });
      document.removeEventListener('touchstart', handleInteraction, { once: true });
    };

    document.addEventListener('click', handleInteraction, { once: true });
    document.addEventListener('touchstart', handleInteraction, { once: true });

    return () => {
      video.removeEventListener('loadeddata', handleLoadedData);
      document.removeEventListener('click', handleInteraction);
      document.removeEventListener('touchstart', handleInteraction);
    };
  }, []);

  // Featured lamps - unique signature pieces with configurator presets
  const featuredLamps = [
    { image: '/images/gallery/pomelli-image (1).png', shade: '6', base: '10', shadeColor: 'ryobix_green', baseColor: 'black' },
    { image: '/images/gallery/pomelli-image (2).png', shade: '3', base: '25', shadeColor: 'orange', baseColor: 'black' },
    { image: '/images/gallery/pomelli-image (7).png', shade: '30', base: '7', shadeColor: 'atomic_pink', baseColor: 'atomic_pink' },
    { image: '/images/gallery/pomelli-image (8).png', shade: '10', base: '8', shadeColor: 'ivory', baseColor: 'atomic_pink' },
    { image: '/images/gallery/pomelli-image (11).png', shade: '1', base: '11', shadeColor: 'teal_blue', baseColor: 'teal_blue' },
    { image: '/images/gallery/pomelli-image (13).png', shade: '13', base: '10', shadeColor: 'lemon_yellow', baseColor: 'black' },
    { image: '/images/gallery/pomelli-image (16).png', shade: '6', base: '12', shadeColor: 'ivory', baseColor: 'black' },
    { image: '/images/gallery/pomelli-image (19).png', shade: '42', base: '11', shadeColor: 'ivory', baseColor: 'teal_blue' },
    { image: '/images/gallery/pomelli-image (22).png', shade: '30', base: '7', shadeColor: 'white', baseColor: 'teal_blue' },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut',
      },
    },
  };

  return (
    <div className="home-page">
      {/* Flying Image to Cart Animation */}
      <AnimatePresence>
        {flyingItem && (
          <motion.img
            src={flyingItem.src}
            className="flying-cart-item"
            initial={{
              x: flyingItem.startX - 40,
              y: flyingItem.startY - 40,
              width: 80,
              height: 80,
              opacity: 1,
              borderRadius: '50%',
            }}
            animate={{
              x: flyingItem.endX - 15,
              y: flyingItem.endY - 15,
              width: 30,
              height: 30,
              opacity: 0.3,
            }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.7, ease: [0.32, 0, 0.67, 0] }}
          />
        )}
      </AnimatePresence>

      {/* Cart Toast Notification */}
      <AnimatePresence>
        {cartToast && (
          <motion.div
            className="cart-toast"
            initial={{ opacity: 0, y: -40, x: '-50%' }}
            animate={{ opacity: 1, y: 0, x: '-50%' }}
            exit={{ opacity: 0, y: -40, x: '-50%' }}
            transition={{ duration: 0.3, delay: 0.5 }}
          >
            {cartToast}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Hero Section with Side-by-Side Layout */}
      <section className="hero-section" ref={heroRef}>
        <div className="hero-container">
          {/* Left: Content */}
          <motion.div
            className="hero-content"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <motion.div
              className="hero-badge"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
            >
              <span className="badge-dot" />
              <span className="badge-text">Handcrafted Excellence</span>
            </motion.div>

            <motion.h1 className="hero-title" variants={itemVariants}>
              <AnimatedText text="Kalpavriksha" className="title-brand" />
              <br />
              <span className="title-subtitle">Studio</span>
            </motion.h1>

            <motion.p className="hero-subtitle" variants={itemVariants}>
              Where artistry meets illumination. Each lamp is meticulously composed from
              interchangeable bases and shades—3D-printed in premium matte PLA—transforming
              your space into a sanctuary of refined elegance.
            </motion.p>

            <motion.div className="hero-actions" variants={itemVariants}>
              <Link to="/design" className="cta-button primary">
                <span className="button-text">Begin Your Design</span>
                <svg className="button-arrow" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M5 12h14M12 5l7 7-7 7"/>
                </svg>
              </Link>
              {!isAuthenticated && (
                <Link to="/login" className="cta-button secondary">
                  <span className="button-text">Sign In</span>
                </Link>
              )}
            </motion.div>

            {/* Elegant Stats */}
            <motion.div
              className="hero-stats"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8, duration: 0.6 }}
            >
              {(() => {
                const baseCount = components?.bases?.length || 0;
                const shadeCount = components?.shades?.length || 0;
                const colorCount = components?.colors?.length || 0;
                const combinations = baseCount * shadeCount * colorCount * colorCount;
                const formatNum = (n) => n > 1000 ? `${Math.floor(n / 1000).toLocaleString()},000+` : String(n);
                return (
                  <>
                    <div className="stat-item">
                      <span className="stat-number">{components ? formatNum(combinations) : '—'}</span>
                      <span className="stat-label">Unique Combinations</span>
                    </div>
                    <div className="stat-divider" />
                    <div className="stat-item">
                      <span className="stat-number">{components ? baseCount : '—'}</span>
                      <span className="stat-label">Base Designs</span>
                    </div>
                    <div className="stat-divider" />
                    <div className="stat-item">
                      <span className="stat-number">{components ? shadeCount : '—'}</span>
                      <span className="stat-label">Shade Patterns</span>
                    </div>
                  </>
                );
              })()}
            </motion.div>
          </motion.div>

          {/* Right: Video */}
          <motion.div
            className="hero-video-container"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <video
              ref={videoRef}
              autoPlay
              loop
              muted
              playsInline
              preload="auto"
              className="hero-video"
            >
              <source src="/images/gallery/intro-video.mov" type="video/mp4" />
            </video>
          </motion.div>
        </div>
      </section>

      {/* Featured Collection */}
      <section className="featured-section">
        <div className="container">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="section-header-left">
              <span className="section-badge">Featured Collection</span>
              <h2 className="section-title">Signature Pieces</h2>
            </div>
            <p className="section-description">
              Each lamp is a testament to precision and artistry—sculptural forms designed
              to feel calm from a distance and rewarding up close. Meticulously crafted to
              bring warmth and sophistication to your home.
            </p>
          </motion.div>

          <div className="featured-grid">
            {featuredLamps.map((lamp, index) => (
              <motion.div
                key={index}
                className="featured-card"
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -8 }}
              >
                <div className="featured-image-wrapper">
                  <img src={lamp.image} alt={`Featured lamp ${index + 1}`} className="featured-image" />
                  <div className="featured-overlay">
                    <button
                      onClick={() => {
                        if (isLampAvailable(lamp)) {
                          navigate(`/design?shade=${lamp.shade}&base=${lamp.base}&shadeColor=${encodeURIComponent(lamp.shadeColor)}&baseColor=${encodeURIComponent(lamp.baseColor)}`);
                        } else {
                          navigate('/design');
                        }
                      }}
                      className="featured-btn"
                    >
                      Customize
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleQuickAddToCart(lamp, e); }}
                      className="featured-btn featured-btn-cart"
                    >
                      Add to Cart
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Perfect For Every Space Section */}
      <section className="perfect-for-section">
        <div className="container">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="section-header-left">
              <span className="section-badge">Perfect For Every Moment</span>
              <h2 className="section-title">Where Light Meets Life</h2>
            </div>
            <p className="section-description">
              Thoughtfully designed to enhance any space and celebrate life's special moments
            </p>
          </motion.div>

          <div className="perfect-for-grid">
            {[
              {
                icon: '🏠',
                title: 'Living Spaces',
                items: ['Bedrooms', 'Living Rooms', 'Home Offices', 'Reading Nooks'],
                gradient: 'linear-gradient(135deg, #B8860B 0%, #9A7309 100%)',
              },
              {
                icon: '🏢',
                title: 'Modern Living',
                items: ['Small Apartments', 'Studios', 'Lofts', 'Co-working Spaces'],
                gradient: 'linear-gradient(135deg, #C5A028 0%, #B8860B 100%)',
              },
              {
                icon: '🎁',
                title: 'Perfect Gifts',
                items: ['Anniversaries', 'Birthdays', 'Housewarmings', 'Graduations'],
                gradient: 'linear-gradient(135deg, #D4AF37 0%, #C5A028 100%)',
              },
              {
                icon: '✨',
                title: 'Special Occasions',
                items: ['Weddings', 'New Home', 'Promotions', 'Milestone Celebrations'],
                gradient: 'linear-gradient(135deg, #B8860B 0%, #8B7355 100%)',
              },
            ].map((category, index) => (
              <motion.div
                key={index}
                className="perfect-for-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -8, transition: { duration: 0.2 } }}
              >
                <div className="perfect-for-icon-wrapper" style={{ background: category.gradient }}>
                  <div className="perfect-for-icon">{category.icon}</div>
                </div>
                <h3 className="perfect-for-title">{category.title}</h3>
                <ul className="perfect-for-list">
                  {category.items.map((item, idx) => (
                    <li key={idx}>{item}</li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Craftsmanship Section */}
      <section className="craftsmanship-section">
        <div className="container">
          <motion.div
            className="craftsmanship-content"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <div className="craftsmanship-text">
              <span className="section-badge">Our Philosophy</span>
              <h2 className="section-title">The Art of Illumination</h2>
              <p className="craftsmanship-description">
                At Kalpavriksha Studio, we believe the objects that light our homes should feel
                as considered as the spaces they inhabit. Each lamp is more than illumination—it's
                a small piece of architecture composed by you.
              </p>
              <p className="craftsmanship-description">
                We blend traditional craftsmanship with modern technology. 3D-printed in premium
                matte PLA with surfaces soft to the touch, our lamps feature clean silhouettes
                and intentionally neutral forms—allowing color, light, and your surroundings to
                take center stage.
              </p>
              <p className="craftsmanship-description">
                With {components?.bases?.length || '—'} base designs, {components?.shades?.length || '—'} shade patterns, and {components?.colors?.length || '—'} curated colors, you can compose
                a lamp that belongs uniquely to your space. As your environment evolves, your lamp
                can evolve with it—swap components instead of replacing the entire piece.
              </p>
              <Link to="/design" className="craftsmanship-cta">
                Begin Your Journey
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M5 12h14M12 5l7 7-7 7"/>
                </svg>
              </Link>
            </div>
            <div className="craftsmanship-features">
              <div className="feature-item">
                <div className="feature-icon">✦</div>
                <h3>Fully Customizable</h3>
                <p>Mix {components?.bases?.length || '—'} bases with {components?.shades?.length || '—'} shades in {components?.colors?.length || '—'} curated colors—see every variation instantly in our configurator</p>
              </div>
              <div className="feature-item">
                <div className="feature-icon">✦</div>
                <h3>Designer-Grade Materials</h3>
                <p>Premium matte PLA with precision details and minimalist silhouettes—refined as gallery objects</p>
              </div>
              <div className="feature-item">
                <div className="feature-icon">✦</div>
                <h3>Modular By Design</h3>
                <p>Components you can reconfigure over time—swap parts instead of replacing the entire lamp</p>
              </div>
              <div className="feature-item">
                <div className="feature-icon">✦</div>
                <h3>Made to Order</h3>
                <p>Printed specifically for your configuration with low-waste, thoughtful production</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <motion.div
          className="cta-content"
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <h2 className="cta-title">Ready to Create Your Masterpiece?</h2>
          <p className="cta-description">
            Design your lamp in real time using our intuitive 3D configurator. Mix bases and shades,
            explore colors, and compose a piece of light architecture that's uniquely yours.
          </p>
          <Link to="/design" className="cta-button primary large">
            <span className="button-text">Start Designing</span>
            <svg className="button-arrow" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M5 12h14M12 5l7 7-7 7"/>
            </svg>
          </Link>
        </motion.div>
      </section>
    </div>
  );
}
